import { getSelectedLanguage } from '@config/i18n';
import {
  addWeeks,
  endOfWeek,
  format,
  getISOWeeksInYear,
  isValid,
  parse,
  setWeek,
  startOfWeek,
  subDays,
} from 'date-fns';
import { enUS, nl } from 'date-fns/locale';

/**
 * Get the start and end dates of a given week number.
 * If the week number is missing, it returns the dates for the current week.
 * @param weekNumber - The week number (1-53). If missing, defaults to the current week.
 * @param year - The year for which the week number is considered. Defaults to the current year.
 * @returns An object containing the startDate and endDate in 'YYYY-MM-DD' format.
 */
export const getWeekDateRange = (weekNumber?: number, year?: number) => {
  const currentDate = new Date();
  const targetYear = year || currentDate.getFullYear();
  const targetWeek = weekNumber || getWeekNumber(currentDate);

  // Set the date to the first day of the target year
  const firstDayOfYear = new Date(targetYear, 0, 1);
  // Set the date to the first day of the target week
  const startOfTargetWeek = setWeek(firstDayOfYear, targetWeek);

  const startDate = format(
    startOfWeek(startOfTargetWeek, { weekStartsOn: 1 }),
    'yyyy-MM-dd',
  );
  const endDate = format(
    endOfWeek(startOfTargetWeek, { weekStartsOn: 1 }),
    'yyyy-MM-dd',
  );

  return { startDate, endDate };
};

/**
 * Get the week number of a given date.
 * @param date - The date for which the week number is calculated.
 * @returns The week number (1-53).
 */
export const getWeekNumber = (date: Date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - startOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
};

/**
 * Get the start and end dates for the last 7 days.
 * @returns An object containing the startDate and endDate in 'YYYY-MM-DD' format.
 */
export const getLast7DaysDateRange = () => {
  const currentDate = new Date();
  const startDate = format(subDays(currentDate, 7), 'yyyy-MM-dd');
  const endDate = format(subDays(currentDate, 1), 'yyyy-MM-dd');

  return { startDate, endDate };
};

/**
 * Get all week numbers for current year.
 * @returns An array of week numbers.
 */
export const getWeekNumbersForCurrentYear = () => {
  const weeksInYear = getISOWeeksInYear(
    new Date(new Date().getFullYear(), 0, 1),
  );
  return Array.from({ length: weeksInYear }, (_, i) => i + 1);
};

/**
 * Get a comma-separated string of all week numbers until the current week.
 * @param currentWeek - The current week number.
 * @returns A comma-separated string of week numbers.
 */
export const getAllWeeksUntilCurrent = (currentWeek: number): string => {
  return Array.from({ length: currentWeek }, (_, i) => i + 1).join(',');
};

/**
 * Custom function to format the date and ensure the month is in camel case.
 * @param date The date to format.
 * returns The formatted date.
 */
export const shortDate = (date: Date) => {
  const locale = getSelectedLanguage() === 'nl' ? nl : enUS;
  const formattedDate = format(date, 'dd MMM', { locale });
  return formattedDate
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace('.', '');
};

/**
 * Get the week data for a specific year, start week, and end week.
 * @param year The year to get the week data for.
 * @param startWeek The start week number.
 * @param endWeek The end week number.
 * @returns An array of objects containing week number, start date, and end date.
 */
export const getWeekDataByYear = (
  year: number,
  startWeek: number,
  endWeek: number,
) => {
  const startOfYear = new Date(year, 0, 1);
  const weeks = [];

  for (let weekIndex = startWeek - 1; weekIndex < endWeek; weekIndex++) {
    const weekStartDate = startOfWeek(addWeeks(startOfYear, weekIndex), {
      weekStartsOn: 1,
    });
    let weekEndDate = endOfWeek(weekStartDate, { weekStartsOn: 1 });

    // Adjust the week number if the weekStartDate is in the next year
    const adjustedWeekNumber =
      weekStartDate.getFullYear() === year ? weekIndex + 1 : 53;

    // Ensure the week does not go into the next year
    if (weekStartDate.getFullYear() > year) {
      break;
    }

    // Adjust the end date for the last week of the year
    if (adjustedWeekNumber === 53) {
      weekEndDate = new Date(year, 11, 31); // December 31 of the current year
    }

    weeks.push({
      weekNumber: adjustedWeekNumber,
      weekString: `W${String(adjustedWeekNumber).padStart(2, '0')}`,
      startDate: shortDate(weekStartDate),
      endDate: shortDate(weekEndDate),
    });
  }

  return weeks;
};

/**
 * Get an array of years from the current year to the start year.
 * @param startYear The year to start from.
 * @returns An array of years.
 */
export const getYearRange = (startYear = 2022) => {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => currentYear - i,
  );
};

/**
 * Converts a 24-hour time format to 12-hour format with AM/PM suffix in the selected language.
 * @param hours The hours in 24-hour format (0-23).
 * @returns A string with time in 12-hour format with localized AM/PM suffix.
 */
export const convertTo12HourFormat = (hours: number): string => {
  const locale = getSelectedLanguage() === 'nl' ? nl : enUS;

  // Create a date object with the provided hours.
  const date = new Date();
  date.setHours(hours);

  // Format the time with localized AM/PM
  // 'h' gives hour in 12h format, 'a' gives am/pm
  return format(date, 'h a', { locale });
};

/**
 * Parse a dashboard metric date string, trying DD-MM-YYYY then falling back to YYYY-MM-DD.
 * Returns a Date on success, or null when the string is absent or unparseable.
 * Both formats appear in stored data depending on which source wrote the date.
 */
export const parseDashboardMetricDate = (dateString: string): Date | null => {
  if (!dateString) {
    return null;
  }

  const referenceDate = new Date();
  const ddMmYyyy = parse(dateString, 'dd-MM-yyyy', referenceDate);

  if (isValid(ddMmYyyy)) {
    return ddMmYyyy;
  }

  const isoDate = parse(dateString, 'yyyy-MM-dd', referenceDate);

  if (isValid(isoDate)) {
    return isoDate;
  }

  return null;
};

/**
 * Format a date value to a short locale-aware string (e.g. "21 Apr 2026").
 * Returns "-" when the value is missing.
 */
export const formatShortDate = (
  dateValue: Date | string | undefined,
): string => {
  if (!dateValue) {
    return '-';
  }
  return new Date(dateValue).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatShortDateNoYear = (
  dateValue: Date | string | undefined,
): string => {
  if (!dateValue) {
    return '-';
  }
  return new Date(dateValue).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * The standard date-only format used across the app (e.g. "2026-04-21").
 * Use this everywhere a bare date is shown so formats stay consistent.
 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Format a date value to the standard ISO date (YYYY-MM-DD).
 * Returns "-" when the value is missing or invalid.
 */
export const formatISODate = (dateValue: Date | string | undefined): string => {
  if (!dateValue) {
    return '-';
  }
  // Already a plain YYYY-MM-DD string - return as-is to avoid timezone shifts
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  const parsedDate = new Date(dateValue);
  if (!isValid(parsedDate)) {
    return '-';
  }
  return format(parsedDate, ISO_DATE_FORMAT);
};

/**
 * Check whether a package is currently active: the `isActive` flag is set
 * and today falls within the `startDate`/`endDate` window (inclusive).
 * Dates are expected in "dd-MM-yyyy" format; a missing bound is treated as
 * unbounded on that side.
 * @param isActive Whether the package is enabled.
 * @param startDate The package's activation date, or null/undefined.
 * @param endDate The package's expiry date, or null/undefined.
 * @returns Whether the package should be treated as active right now.
 */
export const isPackageCurrentlyActive = (
  isActive: boolean,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): boolean => {
  if (!isActive) {
    return false;
  }

  const today = new Date();
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  if (startDate) {
    const parsedStartDate = parse(startDate, 'dd-MM-yyyy', new Date());

    if (!isValid(parsedStartDate)) {
      return false;
    }

    const parsedStartDateOnly = new Date(
      parsedStartDate.getFullYear(),
      parsedStartDate.getMonth(),
      parsedStartDate.getDate(),
    );

    if (todayDateOnly.getTime() < parsedStartDateOnly.getTime()) {
      return false;
    }
  }

  if (endDate) {
    const parsedEndDate = parse(endDate, 'dd-MM-yyyy', new Date());

    if (!isValid(parsedEndDate)) {
      return false;
    }

    const parsedEndDateOnly = new Date(
      parsedEndDate.getFullYear(),
      parsedEndDate.getMonth(),
      parsedEndDate.getDate(),
    );

    if (todayDateOnly.getTime() > parsedEndDateOnly.getTime()) {
      return false;
    }
  }

  return true;
};

/**
 * Format a date string to a more readable format.
 * @param date The date string to format.
 * @returns The formatted date string.
 */
export const formatDate = (date: string | undefined): string => {
  if (!date) {
    return '';
  }

  try {
    // Remove the timezone part (Z or +00:00) and parse as local time
    // To keep the original time without timezone conversion, you have a few options:
    const dateWithoutTimezone = date.replace(/[Z+].*$/, '');
    const parsedDate = new Date(dateWithoutTimezone);

    if (!isValid(parsedDate)) {
      return date;
    }

    return format(parsedDate, 'yyyy-MM-dd HH:mm:ss');
  } catch (error) {
    console.error('Error formatting date:', error);
    return date;
  }
};
